import React, { useCallback } from "react";
import { ErrorMessage, LoadingIndicator } from "@jsq/ds/core";
import { FormTextInput } from "@jsq/ds/forms";
import { formatError, useReportError } from "#common/hooks/useReportError";
import { CountrySelectionDropDown } from "../../../../common/components/CountrySelectionDropDown";
import { FieldGroup } from "../../../../common/components/Form/Layout/FieldGroup";
import { MultiInputFormRow } from "../../../../common/components/Form/Layout/MultiInputFormRow";
import { SimpleFormRow } from "../../../../common/components/Form/Layout/SimpleFormRow";
import { PaymentMethod } from "../../../../common/enums";
import { isUSA } from "../../../../common/utils";
import { SharedAddressInput } from "../../../SharedAddressInput";
import { type CountrySelectProps } from "../../../SharedAddressInput/types";
import { useConsolidatedPaymentPrefContext } from "../../Context";
import { useGetDetailedPaymentPref } from "../../hooks/use-get-detailed-payment-pref";
import { useSetBankCountry } from "../../hooks/use-set-bank-country";
import { getCountryValue, getNameFromCountry } from "../../mappers";
import { AdditionalInstructionsInput } from "../AdditionalInstructionsInput";
import { PaymentPrefLayoutForm } from "../PaymentPrefLayoutForm";
import { type ChildComponentProps } from "../types";
import { BecsBankDetails } from "./BankDetails";
import {
  ADDITIONAL_INSTRUCTIONS_MAX_LENGTH,
  BecsPaymentPrefInputFields,
  BecsPaymentPrefInputSchema,
  type BecsPaymentPrefInputType,
} from "./constants";
import { mapContextStateToFormState, mapFormStateToContextState } from "./utils";

export const BecsAddressCountrySelect: React.FC<CountrySelectProps> = ({
  name,
  className,
  isFull,
  placeholder,
  handleChange,
  label,
}) => (
  <CountrySelectionDropDown
    name={name}
    className={className}
    isFull={isFull}
    placeholder={placeholder}
    handleChange={handleChange}
    getValue={getCountryValue}
    label={label}
  />
);

export const BecsPaymentPrefInput: React.FC<ChildComponentProps> = ({
  onContinueClick,
  onBackClick,
  onBackButtonName,
}) => {
  const { setPaymentPrefDetail, workflowMode, selectedBankCountry } =
    useConsolidatedPaymentPrefContext();
  const { data: paymentPrefDetail, loading, error } = useGetDetailedPaymentPref();

  useReportError(error ? formatError(error) : null);

  const {
    loading: countryLoading,
    error: countryError,
    resolveAndSet,
  } = useSetBankCountry({
    countryCode: "AU",
    paymentPrefDetail,
  });

  const handleFormSubmission = useCallback(
    (values: BecsPaymentPrefInputType) => {
      const bankCountry = selectedBankCountry ?? resolveAndSet({ countryCode: "AU" });
      setPaymentPrefDetail(mapFormStateToContextState(values, bankCountry));
      onContinueClick();
    },
    [setPaymentPrefDetail, onContinueClick, selectedBankCountry, resolveAndSet],
  );

  if (loading || countryLoading) {
    return <LoadingIndicator />;
  }

  if (error || countryError) {
    return <ErrorMessage>Failed to load payment preference</ErrorMessage>;
  }

  return (
    <PaymentPrefLayoutForm
      paymentMethod={PaymentMethod.BECS}
      onSubmit={handleFormSubmission}
      initialValues={mapContextStateToFormState(
        paymentPrefDetail?.__typename === "BecsPaymentPrefObjectType"
          ? paymentPrefDetail
          : undefined,
      )}
      validationSchema={BecsPaymentPrefInputSchema}
      onBackClick={onBackClick}
      onBackButtonName={onBackButtonName}
      workflowMode={workflowMode}
    >
      <BecsBankDetails />
      <FieldGroup title="Account details">
        <SimpleFormRow>
          <FormTextInput
            label="Account holder name"
            isFullWidth
            name={BecsPaymentPrefInputFields.AccountName}
          />
        </SimpleFormRow>
        <MultiInputFormRow>
          <FormTextInput
            label="Account number"
            isFullWidth
            name={BecsPaymentPrefInputFields.AccountNumber}
          />
          <FormTextInput
            label="Confirm account number"
            isFullWidth
            name={BecsPaymentPrefInputFields.ConfirmAccountNumber}
          />
        </MultiInputFormRow>
        <SharedAddressInput
          title="Account holder address"
          CountrySelect={BecsAddressCountrySelect}
          name={BecsPaymentPrefInputFields.Address}
          testIsUSA={(country) => isUSA(getNameFromCountry(country))}
        />
        <SimpleFormRow>
          <AdditionalInstructionsInput
            fieldName={BecsPaymentPrefInputFields.AdditionalInstructions}
            maxLength={ADDITIONAL_INSTRUCTIONS_MAX_LENGTH}
          />
        </SimpleFormRow>
      </FieldGroup>
    </PaymentPrefLayoutForm>
  );
};
